/**
 * This file contains the call to action block.
 */

import { ComponentChildren } from 'preact';
import { Button } from '@components/buttons';
import { EditorProps, ViewerProps } from './types';
import { getProp, setProp } from './editable';
import { getBorderRadiusValue } from '../../../cal/page-builder/theme-styles';
import { CTAState as CTAStateOriginal } from 'server/types';
import { isLight } from 'shared/colors';
import { useState } from 'preact/hooks';
import { CTASettingsMenu } from './settings-menu';

function getBorderRadiusClass(borderRadius?: string): string {
  switch (borderRadius) {
    case 'none':
      return 'rounded-none';
    case 'small':
      return 'rounded-sm';
    case 'medium':
      return 'rounded-lg';
    case 'large':
      return 'rounded-2xl';
    case 'full':
    default:
      return 'rounded-full';
  }
}

type SettingsProps = {
  fullWidth?: boolean;
  defaultChoice?: {
    url: string;
    description: ComponentChildren;
  };
  /**
   * If specified, this trumps whatever is stored in state.
   */
  urlOverride?: string;
};

export type CTAState = CTAStateOriginal;

export type CTAProps = EditorProps<CTAState> &
  SettingsProps & {
    path: string | Array<string | number>;
  };

export const initialState: CTAState = {
  text: 'View Pricing →',
  url: '#pricing',
};

function LoadingButton() {
  return (
    <button class="bg-gray-300 text-gray-500 px-6 py-2 rounded-full inline-flex items-center">
      Loading...
    </button>
  );
}

function buildCtaStyles(callToAction: CTAState): Record<string, any> {
  const styles: Record<string, any> = {};
  if (callToAction.bgcolor) {
    styles.backgroundColor = callToAction.bgcolor;
  }
  if (callToAction.fgcolor) {
    styles.color = callToAction.fgcolor;
  }
  if (callToAction.borderRadius) {
    const borderRadiusValue = getBorderRadiusValue(callToAction.borderRadius);
    if (borderRadiusValue) {
      styles.borderRadius = borderRadiusValue;
    }
  }
  return styles;
}

function buildCtaClasses(callToAction: CTAState, fullWidth: boolean, isEditor: boolean): string {
  const borderRadiusClass = getBorderRadiusClass(callToAction.borderRadius);
  const isBgLight = !!callToAction.bgcolor && isLight(callToAction.bgcolor);
  const baseClasses = `bg-gray-800 hover:shadow-lg px-6 text-lg items-center font-medium ${isBgLight ? 'text-gray-800' : 'text-white'}`;
  const borderRadius = callToAction.borderRadius ? borderRadiusClass : 'rounded-full';
  const layout = fullWidth ? 'w-full justify-center flex' : 'inline-flex justify-center';
  const padding = isEditor && fullWidth ? 'py-4' : 'py-2';

  return [baseClasses, borderRadius, layout, padding, 'call-to-action'].filter(Boolean).join(' ');
}

function CtaButtonEditor(props: CTAProps) {
  const callToAction = props.state;
  const [showSettings, setShowSettings] = useState(false);

  if (!callToAction || !callToAction.text) {
    return <LoadingButton />;
  }

  const buttonClasses = buildCtaClasses(callToAction, props.fullWidth || false, true);
  const inlineStyles = buildCtaStyles(callToAction);

  const btn = (
    <Button class={buttonClasses} style={inlineStyles} onClick={() => setShowSettings(true)}>
      {callToAction.text || initialState.text}
    </Button>
  );
  if (!showSettings) {
    return btn;
  }

  return (
    <div class="relative inline-flex">
      <CTASettingsMenu
        state={props.state}
        setState={props.setState}
        close={() => setShowSettings(false)}
        onDelete={() => {}}
      />
      {btn}
    </div>
  );
}

function CtaButtonViewer(props: { state: CTAState; fullWidth?: boolean; urlOverride?: string }) {
  const callToAction = props.state;

  if (!callToAction || !callToAction.text) {
    return <LoadingButton />;
  }

  const url = props.urlOverride || props.state.url;
  const isHash = url?.startsWith('#');

  const buttonClasses = buildCtaClasses(callToAction, props.fullWidth || false, false);
  const inlineStyles = buildCtaStyles(callToAction);

  return (
    <Button
      class={buttonClasses}
      style={inlineStyles}
      href={isHash ? undefined : url}
      onClick={
        isHash
          ? (e) => {
              e.preventDefault();
              document.querySelector(url)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
          : undefined
      }
    >
      {callToAction.text || initialState.text}
    </Button>
  );
}

type CtaButtonProps = (EditorProps<any> | ViewerProps<any>) & {
  path: string | Array<string | number>;
  fullWidth?: boolean;
  defaultChoice?: {
    url: string;
    description: ComponentChildren;
  };
  urlOverride?: string;
};

export function CtaButton(props: CtaButtonProps) {
  const readonly = !(props as EditorProps<CTAState>).setState;
  const val = getProp(props);

  if (readonly) {
    return (
      <CtaButtonViewer
        state={{ ...val, url: val?.url || props.defaultChoice?.url }}
        fullWidth={props.fullWidth}
        urlOverride={props.urlOverride}
      />
    );
  }

  const editorProps = props as EditorProps<CTAState>;

  return (
    <CtaButtonEditor
      {...editorProps}
      path={props.path}
      state={val}
      setState={(f: any) => setProp(props, f)}
      fullWidth={props.fullWidth}
      defaultChoice={props.defaultChoice}
      urlOverride={props.urlOverride}
    />
  );
}
